import React, { lazy, useEffect, useState } from 'react';
import './App.css';
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { getCurrentUserInfo, setCurrentUserInfo, removeCurrentUserInfo } from '@services/Cookies';
import PrefetchBootstrap from '@common/PrefetchBootstrap';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import './i18n';
import { GlobalStyle } from '@styles/Common';
import { fetchCurrentUser } from '@services/api/auth';
import { CurrentUser } from '@typeList/api/user';
import { useSessionKeepAlive } from '@hooks/useSessionKeepAlive';
import { syncApplicationLanguage } from '@utils/language';

// 遅延ロードするコンポーネント
const MyPage = lazy(() => import('@pages/MyPage'));
const LogIn = lazy(() => import('@pages/LogIn'));
const ProcessView = lazy(() => import('@pages/process/ProcessView'));
const CaseStudyView = lazy(() => import('@pages/case_study/CaseStudyView'));
const UserManagement = lazy(() => import('@pages/admin/UserManagement'));
const MasterManagement = lazy(() => import('@pages/admin/MasterManagement'));
const SharedFileManagement = lazy(() => import('@pages/admin/SharedFileManagement'))
const SystemInfo = lazy(() => import('@pages/admin/SystemInfo'))
const ApiManagement = lazy(() => import('@pages/admin/ApiManagement'))
const TabDisplayBySanKeyDiagram = lazy(() => import('@pages/TabDisplayBySankeyDiagram'))
const NotFound = lazy(() => import('@pages/NotFound'));

interface AuthGuardProps {
    component: React.ReactElement;
}

const useEnsureCurrentUser = (shouldFetchRemote: boolean = true) => {
    const [user, setUser] = useState<CurrentUser | null | undefined>(undefined);

    useEffect(() => {
        let cancelled = false;
        const localUser = getCurrentUserInfo();
        if (localUser) {
            syncApplicationLanguage(localUser.default_locale_code);
            setUser(localUser);
            return () => { cancelled = true; };
        }

        if (!shouldFetchRemote) {
            setUser(null);
            return () => {
                cancelled = true;
            };
        }

        (async () => {
            try {
                const remoteUser = await fetchCurrentUser();
                if (cancelled) {
                    return;
                }
                syncApplicationLanguage(remoteUser.default_locale_code);
                setCurrentUserInfo(JSON.stringify(remoteUser));
                setUser(remoteUser);
            } catch (error) {
                if (cancelled) {
                    return;
                }
                removeCurrentUserInfo();
                setUser(null);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [shouldFetchRemote]);

    return user;
};

const RequireAuth = ({ component }: AuthGuardProps) => {
    const user = useEnsureCurrentUser();
    if (user === undefined) {
        return null;
    }
    if (user === null) {
        document.location = "/login";
        return null;
    }
    return component;
};

const RequireNoAuth = ({ component }: AuthGuardProps) => {
    const user = useEnsureCurrentUser(false);
    if (user === undefined) {
        return null;
    }
    if (user) {
        document.location = "/";
        return null;
    }
    return component;
};

const RequireAdmin = ({ component }: AuthGuardProps) => {
    const user = useEnsureCurrentUser();
    if (user === undefined) {
        return null;
    }
    if (user === null) {
        document.location = "/login";
        return null;
    }
    if (user.is_admin) {
        return component;
    }
    document.location = "/";
    return null;
};

const App = () => {
    useSessionKeepAlive();

    return (
        <Router>
            <DndProvider backend={HTML5Backend}>
                <GlobalStyle />
                {/* ログイン後共通でプリフェッチを実行（自動ログイン含む） */}
                <PrefetchBootstrap />
                <Routes>
                    {/* 全ユーザー */}
                    <Route path="/login" element={<RequireNoAuth component={<LogIn />} />} />
                    <Route path="/" element={<RequireAuth component={<MyPage />} />} />
                    <Route path="/process/process_view/:id" element={<RequireAuth component={<ProcessView />} />} />
                    <Route path="/case_study/case_study_view/:id" element={<RequireAuth component={<CaseStudyView />} />} />
                    <Route path="/sankey_diagram_view" element={<RequireAuth component={<TabDisplayBySanKeyDiagram />} />} />
                    <Route path="*" element={<NotFound />} />
                    {/* 管理者 */}
                    <Route path="/admin/user_management" element={<RequireAdmin component={<UserManagement />} />} />
                    <Route path="/admin/master" element={<RequireAdmin component={<MasterManagement />} />} />
                    <Route path="/admin/shared_file_management" element={<RequireAdmin component={<SharedFileManagement />} />} />
                    <Route path="/admin/system_info" element={<RequireAdmin component={<SystemInfo />} />} />
                    <Route path="/admin/api_management" element={<RequireAdmin component={<ApiManagement />} />} />
                </Routes>
            </DndProvider>
        </Router>
    );
};

export default App;
